import { clientApiFetch } from "@/lib/api/client";
import type {
  ForumActor,
  ForumAttachment,
  ForumComment,
  ForumPost,
  ForumPostDetail,
  ForumPostFilters,
  ForumPostListResponse,
  ForumPostPayload,
  ForumReactionEmoji,
  ForumTopic,
  ForumTopicPayload,
  ForumVisibilityOptions,
} from "../types";

const BASE_URL = "/api/forum";

export function listForumTopics() {
  return clientApiFetch<ForumTopic[]>(`${BASE_URL}/topics`);
}

export function createForumTopic(payload: ForumTopicPayload) {
  return clientApiFetch<ForumTopic>(`${BASE_URL}/topics`, {
    body: JSON.stringify(payload),
    method: "POST",
  });
}

export function updateForumTopic(id: string, payload: ForumTopicPayload) {
  return clientApiFetch<ForumTopic>(`${BASE_URL}/topics/${id}`, {
    body: JSON.stringify(payload),
    method: "PATCH",
  });
}

export function listForumPosts(filters: ForumPostFilters = {}) {
  const query = buildPostQuery(filters);
  return clientApiFetch<ForumPostListResponse>(
    `${BASE_URL}/posts${query ? `?${query}` : ""}`,
  );
}

export function createForumPost(payload: ForumPostPayload) {
  return clientApiFetch<ForumPostDetail>(`${BASE_URL}/posts`, {
    body: JSON.stringify(payload),
    method: "POST",
  });
}

export function getForumPost(id: string) {
  return clientApiFetch<ForumPostDetail>(`${BASE_URL}/posts/${id}`);
}

export function listForumMentionUsers(query = "") {
  const params = new URLSearchParams();
  if (query.trim()) params.set("q", query.trim());
  const suffix = params.toString();
  return clientApiFetch<ForumActor[]>(
    `${BASE_URL}/mention-users${suffix ? `?${suffix}` : ""}`,
  );
}

export function listForumVisibilityOptions() {
  return clientApiFetch<ForumVisibilityOptions>(`${BASE_URL}/visibility-options`);
}

export function uploadForumAttachment(input: {
  commentId?: string;
  draftId?: string;
  file: File;
  inline?: boolean;
  postId?: string;
  scope: "POST" | "COMMENT";
}) {
  const formData = new FormData();
  formData.set("file", input.file);
  formData.set("scope", input.scope);
  if (input.commentId) formData.set("commentId", input.commentId);
  if (input.draftId) formData.set("draftId", input.draftId);
  if (input.inline) formData.set("inline", "true");
  if (input.postId) formData.set("postId", input.postId);

  return clientApiFetch<ForumAttachment>(`${BASE_URL}/attachments`, {
    body: formData,
    method: "POST",
  });
}

export function deleteForumAttachment(id: string) {
  return clientApiFetch<ForumAttachment>(`${BASE_URL}/attachments/${id}`, {
    method: "DELETE",
  });
}

export function updateForumPost(
  id: string,
  payload: Partial<ForumPostPayload>,
) {
  return clientApiFetch<ForumPostDetail>(`${BASE_URL}/posts/${id}`, {
    body: JSON.stringify(payload),
    method: "PATCH",
  });
}

export function deleteForumPost(id: string, reason?: string) {
  return clientApiFetch<{ id: string; status: "DELETED" }>(
    `${BASE_URL}/posts/${id}`,
    {
      body: JSON.stringify({ reason }),
      method: "DELETE",
    },
  );
}

export function createForumComment(
  postId: string,
  content: string,
  options: { contentFormat?: "html" | "plain"; draftId?: string } = {},
) {
  return clientApiFetch<ForumComment>(`${BASE_URL}/posts/${postId}/comments`, {
    body: JSON.stringify({ content, ...options }),
    method: "POST",
  });
}

export function updateForumComment(
  commentId: string,
  content: string,
  options: { contentFormat?: "html" | "plain" } = {},
) {
  return clientApiFetch<ForumComment>(`${BASE_URL}/comments/${commentId}`, {
    body: JSON.stringify({ content, ...options }),
    method: "PATCH",
  });
}

export function deleteForumComment(commentId: string, reason?: string) {
  return clientApiFetch<ForumComment>(`${BASE_URL}/comments/${commentId}`, {
    body: JSON.stringify({ reason }),
    method: "DELETE",
  });
}

export function followForumPost(id: string) {
  return clientApiFetch<ForumPostDetail>(`${BASE_URL}/posts/${id}/follow`, {
    method: "POST",
  });
}

export function unfollowForumPost(id: string) {
  return clientApiFetch<ForumPostDetail>(`${BASE_URL}/posts/${id}/follow`, {
    method: "DELETE",
  });
}

export function closeForumPost(id: string, reason?: string) {
  return clientApiFetch<ForumPostDetail>(`${BASE_URL}/posts/${id}/close`, {
    body: JSON.stringify({ reason }),
    method: "POST",
  });
}

export function reopenForumPost(id: string, reason?: string) {
  return clientApiFetch<ForumPostDetail>(`${BASE_URL}/posts/${id}/reopen`, {
    body: JSON.stringify({ reason }),
    method: "POST",
  });
}

export function pinForumPost(id: string) {
  return clientApiFetch<ForumPost>(`${BASE_URL}/posts/${id}/pin`, {
    method: "POST",
  });
}

export function unpinForumPost(id: string) {
  return clientApiFetch<ForumPost>(`${BASE_URL}/posts/${id}/pin`, {
    method: "DELETE",
  });
}

export function setForumReaction(id: string, emoji: ForumReactionEmoji) {
  return clientApiFetch<ForumPostDetail>(`${BASE_URL}/posts/${id}/reaction`, {
    body: JSON.stringify({ emoji }),
    method: "PUT",
  });
}

export function removeForumReaction(id: string) {
  return clientApiFetch<ForumPostDetail>(`${BASE_URL}/posts/${id}/reaction`, {
    method: "DELETE",
  });
}

function buildPostQuery(filters: ForumPostFilters) {
  const query = new URLSearchParams();

  if (filters.q?.trim()) query.set("q", filters.q.trim());
  if (filters.status) query.set("status", filters.status);
  if (filters.topicId) query.set("topicId", filters.topicId);
  if (filters.mine) query.set("mine", "true");
  if (filters.following) query.set("following", "true");
  if (filters.pinned) query.set("pinned", "true");
  if (filters.page) query.set("page", String(filters.page));
  if (filters.limit) query.set("limit", String(filters.limit));

  return query.toString();
}
